/**
 * Module pur : calcul TURPE 7 HTA-BT (Délibération CRE N°2025-78 du 13 mars 2025).
 *
 * Aucune dépendance à React. Toutes les constantes et fonctions sont stables et
 * peuvent être importées comme module-level constants — ce qui évite de les
 * recréer à chaque rendu de App.js et permet de tester unitairement sans monter
 * un composant React.
 */

// ============================================
// SEGMENTS CLIENTS ÉLECTRICITÉ (C1 à C5)
// ============================================
export const SEGMENTS_ELEC = {
  C5: {
    label: 'C5 - Résidentiel / Petit Pro',
    description: '≤ 36 kVA, Tarif Bleu',
    puissanceMax: 36,
    domaine: 'BT_36',
  },
  C4: {
    label: 'C4 - Professionnel Moyen',
    description: '36 - 250 kVA, Tarif Jaune',
    puissanceMin: 36,
    puissanceMax: 250,
    domaine: 'BT_SUP36',
  },
  C3: {
    label: 'C3 - Grand Professionnel',
    description: '250 kVA - 2 MW, Tarif Vert A5/A8',
    puissanceMin: 250,
    puissanceMax: 2000,
    domaine: 'HTA',
  },
  C2: {
    label: 'C2 - Gros Consommateur',
    description: '2 - 10 MW, Tarif Vert',
    puissanceMin: 2000,
    puissanceMax: 10000,
    domaine: 'HTA',
  },
  C1: {
    label: 'C1 - Très Gros Consommateur',
    description: '> 10 MW, Raccordé HTB',
    puissanceMin: 10000,
    domaine: 'HTB',
  },
};

// Composantes fixes (€/an) - CG (Gestion) + CC (Comptage)
// Source: Tableau 35 et Tableau 36 - Délibération CRE 2025-78
export const COMPOSANTES_FIXES = {
  HTA: { gestion: 257.14, comptage: 376.39 },
  BT_SUP36: { gestion: 217.80, comptage: 283.27 },
  BT_36: { gestion: 9.00, comptage: 22.00 },
};

// Coefficients pondérateurs de PUISSANCE (bi) - €/kW/an
// Source: Tableau 43, 44, 46 - Délibération CRE 2025-78
export const COEFF_PUISSANCE = {
  HTA: {
    CU: { POINTE: 14.41, HPH: 14.41, HCH: 14.41, HPE: 12.55, HCE: 11.22 },
    LU: { POINTE: 35.33, HPH: 32.30, HCH: 20.39, HPE: 14.33, HCE: 11.56 },
  },
  BT_SUP36: {
    CU: { HPH: 17.61, HCH: 15.96, HPE: 14.56, HCE: 11.98 },
    LU: { HPH: 30.16, HCH: 21.18, HPE: 16.64, HCE: 12.37 },
  },
  BT_36: {
    CU: { UNIQUE: 10.11 },
    MU: { UNIQUE: 10.11 },
    LU: { UNIQUE: 10.11 },
  },
};

// Coefficients pondérateurs d'ÉNERGIE (ci) - €/kWh
// Source: Tableau 43, 44, 46 - Délibération CRE 2025-78
export const COEFF_ENERGIE = {
  HTA: {
    CU: { POINTE: 0.0574, HPH: 0.0423, HCH: 0.0199, HPE: 0.0101, HCE: 0.0069 },
    LU: { POINTE: 0.0265, HPH: 0.0210, HCH: 0.0147, HPE: 0.0092, HCE: 0.0068 },
  },
  BT_SUP36: {
    CU: { HPH: 0.0691, HCH: 0.0421, HPE: 0.0213, HCE: 0.0152 },
    LU: { HPH: 0.0569, HCH: 0.0347, HPE: 0.0201, HCE: 0.0149 },
  },
  BT_36: {
    CU: { HPH: 0.0749, HCH: 0.0397, HPE: 0.0166, HCE: 0.0116 },
    MU: { HPH: 0.0700, HCH: 0.0373, HPE: 0.0161, HCE: 0.0111 },
    LU: { ALL: 0.0125 },
  },
};

// Taux CTA (15% - Contribution Tarifaire d'Acheminement, sur CG + CC + Soutirage puissance)
export const TAUX_CTA = 0.15;
// Taux TVA (20%)
export const TAUX_TVA = 0.20;

/**
 * Détermine le domaine de tension selon la puissance souscrite et le segment client.
 */
export function getDomaineTension(puissanceKVA, segment) {
  if (segment && SEGMENTS_ELEC[segment]) {
    return SEGMENTS_ELEC[segment].domaine;
  }
  if (puissanceKVA <= 36) return 'BT_36';
  if (puissanceKVA <= 250) return 'BT_SUP36';
  return 'HTA';
}

/**
 * Récupère les composantes fixes (gestion, comptage) du domaine.
 */
export function getComposantesFixes(puissanceKVA, segment) {
  const domaine = getDomaineTension(puissanceKVA, segment);
  return COMPOSANTES_FIXES[domaine] || COMPOSANTES_FIXES.BT_36;
}

const EMPTY_TURPE = {
  turpeHT: 0, cta: 0, baseCTA: 0, total: 0, totalTTC: 0, tva: 0,
  partFixePuissance: 0, partVariable: 0, gestion: 0, comptage: 0,
  domaine: 'BT_36',
};

/**
 * Calcul du TURPE 7 + CTA + TVA.
 * Formule: CS = Σ[bi × Pi] + Σ[ci × Ei]
 *
 * @param {number} puissanceKVA   Puissance souscrite en kVA
 * @param {string} option         CU | MU | LU
 * @param {Object} consoPeriods   { POINTE, HPH, HCH, HPE, HCE } en MWh
 * @param {string|null} segment   C1..C5 ou null
 */
export function calculTURPE(puissanceKVA, option, consoPeriods, segment = null) {
  if (!option || puissanceKVA <= 0) {
    return { ...EMPTY_TURPE };
  }

  const domaine = getDomaineTension(puissanceKVA, segment);
  const composantesFixes = getComposantesFixes(puissanceKVA, segment);

  const coeffPuissance = COEFF_PUISSANCE[domaine]?.[option] || COEFF_PUISSANCE.BT_36.MU;
  const coeffEnergie = COEFF_ENERGIE[domaine]?.[option] || COEFF_ENERGIE.BT_36.MU;

  // Conversion kVA → kW (simplification : on garde la même valeur)
  const puissanceKW = puissanceKVA;

  // Part PUISSANCE (soutirage fixe)
  let partFixePuissance = 0;
  if (coeffPuissance.UNIQUE !== undefined) {
    partFixePuissance = puissanceKW * coeffPuissance.UNIQUE;
  } else {
    const biMax = Math.max(
      coeffPuissance.POINTE || 0,
      coeffPuissance.HPH || 0,
      coeffPuissance.HCH || 0,
      coeffPuissance.HPE || 0,
      coeffPuissance.HCE || 0
    );
    partFixePuissance = puissanceKW * biMax;
  }

  // Part ÉNERGIE (consommation en MWh → kWh × coeff €/kWh)
  const consoPointe = (consoPeriods.POINTE || 0) * 1000;
  const consoHPH = (consoPeriods.HPH || 0) * 1000;
  const consoHCH = (consoPeriods.HCH || 0) * 1000;
  const consoHPE = (consoPeriods.HPE || 0) * 1000;
  const consoHCE = (consoPeriods.HCE || 0) * 1000;

  let partVariable = 0;
  if (coeffEnergie.ALL !== undefined) {
    partVariable = (consoPointe + consoHPH + consoHCH + consoHPE + consoHCE) * coeffEnergie.ALL;
  } else {
    partVariable =
      consoPointe * (coeffEnergie.POINTE || 0) +
      consoHPH * (coeffEnergie.HPH || 0) +
      consoHCH * (coeffEnergie.HCH || 0) +
      consoHPE * (coeffEnergie.HPE || 0) +
      consoHCE * (coeffEnergie.HCE || 0);
  }

  const turpeHT = composantesFixes.gestion + composantesFixes.comptage + partFixePuissance + partVariable;

  // CTA (15%) — base = CG + CC + part fixe puissance (sans énergie)
  const baseCTA = composantesFixes.gestion + composantesFixes.comptage + partFixePuissance;
  const cta = baseCTA * TAUX_CTA;
  const total = turpeHT + cta;
  const tva = total * TAUX_TVA;
  const totalTTC = total + tva;

  return {
    turpeHT, cta, baseCTA, total, tva, totalTTC,
    partFixePuissance, partVariable,
    gestion: composantesFixes.gestion,
    comptage: composantesFixes.comptage,
    domaine,
  };
}
